import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  LuPencilLine,
  LuSettings,
  LuLayers,
  LuCircleHelp,
  LuLogOut,
  LuTrash2,
  LuChevronRight,
  LuMessageCircle,
} from 'react-icons/lu';
import XiPageShell from '../components/xi/XiPageShell';
import useSessionUser from '../hooks/useSessionUser';

function initialsOf(displayName) {
  const raw = (displayName || '').trim();
  if (!raw) return 'Xi';
  const base = raw.includes('@') ? raw.split('@')[0] : raw;
  const parts = base.replace(/[._-]+/g, ' ').trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return base.slice(0, 2).toUpperCase();
}

const LINKS = [
  {
    to: '/profile/edit',
    icon: LuPencilLine,
    title: 'Edit profile',
    sub: 'Name, age, gender, phone, job title and language',
  },
  {
    to: '/settings',
    icon: LuSettings,
    title: 'Settings',
    sub: 'Appearance, language and account controls',
  },
  {
    to: '/plans',
    icon: LuLayers,
    title: 'Subscription plans',
    sub: 'Compare plans and manage your subscription',
  },
  {
    to: '/help',
    icon: LuCircleHelp,
    title: 'Help & support',
    sub: 'Answers to common questions',
  },
];

export default function Profile() {
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useSessionUser();

  return (
    <XiPageShell
      eyebrow="Account"
      title="Your profile"
      lede="Everything about your Xi account in one place."
    >
      <section className="xi-card xi-profile-hero">
        <span className="xi-avatar xi-avatar--lg" aria-hidden="true">
          {initialsOf(user.displayName || user.name)}
        </span>
        <div className="xi-profile-hero__text">
          <h2 className="xi-profile-hero__name">{user.displayName || user.name || 'Guest'}</h2>
          <p className="xi-profile-hero__meta">
            {user.email && user.email.includes('@') ? user.email : 'Signed in'}
            {user.role ? ` · ${user.role}` : ''}
          </p>
          {user.id && (
            <p className="xi-profile-hero__meta" style={{ fontSize: '0.78rem', opacity: 0.75 }}>
              User ID {user.id}
            </p>
          )}
        </div>
      </section>

      {!isAuthenticated && (
        <div className="xi-error" style={{ marginTop: 18 }} role="status">
          You are not signed in. Sign in to see your account details and reports.
          <Link to="/login" style={{ marginLeft: 8, textDecoration: 'underline' }}>
            Sign in
          </Link>
        </div>
      )}

      <section className="xi-section">
        <h2 className="xi-section__title">Account</h2>
        <div className="xi-grid-2">
          {LINKS.map((item) => {
            const Icon = item.icon;
            return (
              <Link key={item.to} to={item.to} className="xi-link-card">
                <span className="xi-link-card__icon" aria-hidden="true">
                  <Icon />
                </span>
                <span className="xi-link-card__text">
                  <span className="xi-link-card__title">{item.title}</span>
                  <span className="xi-link-card__sub">{item.sub}</span>
                </span>
                <LuChevronRight className="xi-link-card__chevron" aria-hidden="true" />
              </Link>
            );
          })}
        </div>
      </section>

      <section className="xi-section">
        <h2 className="xi-section__title">Talk to Xi</h2>
        <Link to="/chatbot" className="xi-link-card">
          <span className="xi-link-card__icon" aria-hidden="true">
            <LuMessageCircle />
          </span>
          <span className="xi-link-card__text">
            <span className="xi-link-card__title">Open the chatbot</span>
            <span className="xi-link-card__sub">Voice check-ins with Xi</span>
          </span>
          <LuChevronRight className="xi-link-card__chevron" aria-hidden="true" />
        </Link>
      </section>

      <section className="xi-section">
        <h2 className="xi-section__title">Session</h2>
        <div className="xi-card" style={{ padding: '6px 20px' }}>
          <div className="xi-row">
            <div className="xi-row__text">
              <span className="xi-row__label">Log out</span>
              <span className="xi-row__hint">End this session on this device.</span>
            </div>
            <button type="button" className="xi-btn xi-btn--soft" onClick={logout}>
              <LuLogOut aria-hidden="true" />
              Log out
            </button>
          </div>
          <div className="xi-row">
            <div className="xi-row__text">
              <span className="xi-row__label">Deactivate account</span>
              <span className="xi-row__hint">Permanently remove your account and data.</span>
            </div>
            <button
              type="button"
              className="xi-btn xi-btn--danger"
              style={{ height: 42, fontSize: '0.86rem' }}
              onClick={() => navigate('/delete')}
            >
              <LuTrash2 aria-hidden="true" />
              Deactivate
            </button>
          </div>
        </div>
      </section>
    </XiPageShell>
  );
}
